import { useState } from 'react';
import { register, verify, login, requestPasswordReset, confirmPasswordReset } from '../api/auth';
import './AuthPage.css';

export default function AuthPage({ onLogin }) {
  const [mode, setMode] = useState('login'); // login | register | verify | forgot | reset
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  async function handleLogin() {
    setLoading(true);
    setError(null);
    try {
      await login(email, password);
      onLogin();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRegister() {
    setLoading(true);
    setError(null);
    try {
      await register(email, password);
      setMessage('Check your email for a verification code.');
      setMode('verify');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleVerify() {
    setLoading(true);
    setError(null);
    try {
      await verify(email, code);
      setMessage('Email verified! You can now log in.');
      setMode('login');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleRequestReset() {
    setLoading(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      setMessage('Check your email for a reset code.');
      setMode('reset');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleConfirmReset() {
    setLoading(true);
    setError(null);
    try {
      await confirmPasswordReset(email, code, password);
      setMessage('Password reset! You can now log in.');
      setPassword('');
      setCode('');
      setMode('login');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-container">
      <div className="auth-box">
        <h1 className="auth-title">HIDENN AI</h1>

        {mode === 'login' && (
          <>
            <h2>Sign in</h2>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <div className="auth-error">{error}</div>}
            <button onClick={handleLogin} disabled={loading}>
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            <p className="auth-switch">
              Don't have an account?{' '}
              <span
                onClick={() => {
                  setMode('register');
                  setError(null);
                }}
              >
                Register
              </span>
            </p>
            <p className="auth-switch">
              <span
                onClick={() => {
                  setMode('forgot');
                  setError(null);
                  setMessage(null);
                }}
              >
                Forgot password?
              </span>
            </p>
          </>
        )}

        {mode === 'register' && (
          <>
            <h2>Create account</h2>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password (min 8 chars, upper, lower, number)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <div className="auth-error">{error}</div>}
            {message && <div className="auth-message">{message}</div>}
            <button onClick={handleRegister} disabled={loading}>
              {loading ? 'Creating account...' : 'Create account'}
            </button>
            <p className="auth-switch">
              Already have an account?{' '}
              <span
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
              >
                Sign in
              </span>
            </p>
          </>
        )}

        {mode === 'verify' && (
          <>
            <h2>Verify your email</h2>
            {message && <div className="auth-message">{message}</div>}
            <input
              type="text"
              placeholder="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            {error && <div className="auth-error">{error}</div>}
            <button onClick={handleVerify} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify email'}
            </button>
            <p className="auth-switch">
              Wrong email?{' '}
              <span
                onClick={() => {
                  setMode('register');
                  setError(null);
                }}
              >
                Go back
              </span>
            </p>
          </>
        )}

        {mode === 'forgot' && (
          <>
            <h2>Reset password</h2>
            <input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            {error && <div className="auth-error">{error}</div>}
            {message && <div className="auth-message">{message}</div>}
            <button onClick={handleRequestReset} disabled={loading}>
              {loading ? 'Sending...' : 'Send reset code'}
            </button>
            <p className="auth-switch">
              <span
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
              >
                Back to sign in
              </span>
            </p>
          </>
        )}

        {mode === 'reset' && (
          <>
            <h2>Set a new password</h2>
            {message && <div className="auth-message">{message}</div>}
            <input
              type="text"
              placeholder="Reset code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />
            <input
              type="password"
              placeholder="New password (min 8 chars, upper, lower, number)"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && <div className="auth-error">{error}</div>}
            <button onClick={handleConfirmReset} disabled={loading}>
              {loading ? 'Resetting...' : 'Reset password'}
            </button>
            <p className="auth-switch">
              <span
                onClick={() => {
                  setMode('login');
                  setError(null);
                }}
              >
                Back to sign in
              </span>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
